import { useState } from 'react'
import { RefreshCw, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useRevalidateToken, RevalidateError } from '@/hooks/useTokenHealth'
import type { TokenStatus } from '@/hooks/useTokenHealth'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  readonly locationId: string
  readonly tokenStatus: TokenStatus
  readonly middlewareUrl: string | null
  /** icon-only: compact for list cards; with-label: full text for detail header */
  readonly variant?: 'icon-only' | 'with-label'
}

/**
 * Which confirmation dialog is currently open (if any).
 *
 * Using a single enum state instead of two booleans prevents the impossible
 * "both dialogs open" state and keeps the dialog render path to a single
 * branch.
 */
type ConfirmAction = 'revalidate' | 'reinstall' | null

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TokenActions({
  locationId,
  middlewareUrl,
  variant = 'icon-only',
}: Props) {
  const revalidate = useRevalidateToken()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null)

  // ─── Action implementations (called after user confirms) ──────────────
  function doRevalidate() {
    setErrorMsg(null)
    revalidate.mutate(locationId, {
      onError: (err) => {
        if (err instanceof RevalidateError && err.requiresReinstall) {
          setErrorMsg('Reinstall required')
        } else {
          setErrorMsg('Refresh failed')
        }
      },
      onSuccess: () => {
        setErrorMsg(null)
      },
    })
  }

  function doReinstall() {
    if (!middlewareUrl) return
    // Called inside the AlertDialogAction click handler — which IS a user
    // gesture, so popup blockers permit window.open. Opening the OAuth URL
    // in a new tab preserves the old <a target="_blank"> behavior.
    window.open(`${middlewareUrl}/oauth/authorize`, '_blank', 'noopener,noreferrer')
  }

  function handleConfirm() {
    if (confirmAction === 'revalidate') doRevalidate()
    else if (confirmAction === 'reinstall') doReinstall()
    setConfirmAction(null)
  }

  const isPending = revalidate.isPending

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {/* Error message */}
      {errorMsg && (
        <span className="text-[10px] text-red-500 font-medium whitespace-nowrap">
          {errorMsg}
        </span>
      )}

      {/* Revalidate button — opens confirm dialog */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-1.5 text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation()
                setConfirmAction('revalidate')
              }}
              disabled={isPending}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isPending ? 'animate-spin' : ''}`} />
              {variant === 'with-label' && (
                <span className="ml-1.5 text-xs">Revalidate</span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Force-refresh this location&apos;s OAuth token
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Reinstall button — opens confirm dialog */}
      {middlewareUrl && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation()
                  setConfirmAction('reinstall')
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {variant === 'with-label' && (
                  <span className="ml-1.5 text-xs">Reinstall</span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Re-authorize this location in GHL (opens OAuth consent screen)
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Confirmation dialog — content switches on which action is pending */}
      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null)
        }}
      >
        <AlertDialogContent>
          {confirmAction === 'revalidate' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Revalidate OAuth token?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will force-refresh the GoHighLevel OAuth token for this
                  location by exchanging the stored refresh token for a new
                  access token. Use this if you suspect the token is expired
                  or the middleware is using a stale one.
                  <br />
                  <br />
                  If GHL rejects the refresh token (e.g., the app was
                  uninstalled remotely), you&apos;ll see a &quot;Reinstall required&quot; error
                  and need to reauthorize the app instead.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirm}>
                  Revalidate
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : confirmAction === 'reinstall' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Reinstall GHL app for this location?</AlertDialogTitle>
                <AlertDialogDescription>
                  You&apos;ll be redirected to GoHighLevel&apos;s OAuth consent screen
                  in a new tab. After you authorize, the stored tokens for
                  this location will be replaced with fresh ones.
                  <br />
                  <br />
                  Background jobs (cron follow-ups, qualification, webhooks)
                  that were using the current token may briefly fail until
                  the new token is saved. Only reinstall if &quot;Revalidate&quot;
                  didn&apos;t resolve the issue.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirm}>
                  Continue to GHL
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
